import {
  againField,
  characterField,
  stageField,
} from "../content/lianxi/exercise-fields.js";
import type { DrillProgress } from "../handwriting/xiezi-stages.js";
import { type HandoverCount, answerHandoverCount } from "./lianxi-handover.js";
import type { ExerciseKeeper } from "./lianxi-keeper.js";
import {
  ExercisePress,
  type RecordAnswer,
  answeredPlaces,
} from "./lianxi-post.js";
import { remoteMarker } from "./lianxi-remote-marker.js";
import type { CharacterMarker } from "./xiezi-check.js";

export { unsentLine } from "./lianxi-remote-marker.js";

/**
 * Keeping a reader's place in an Exercise 练习 through the `/user/lianxi/`
 * handler.
 *
 * The handler's page for a drill carries where a press posts, and this is the
 * press. A pass done in a stage that snaps is reported with the stage it was
 * in. A pass written from memory is posted as ink by `lianxi-remote-marker.ts`
 * and marked there. Pressing Do the drill again posts that. Every answer
 * carries the places as the handler now holds them, in the Exercise's order,
 * and the Exercise adopts them. docs/lianxi.md under "Keeping a reader's
 * place" has the exchange, and `src/user/lianxi-page.ts` is the other end.
 *
 * **A press that cannot reach the handler moves the page on its own
 * arithmetic**, and says so on a pass written from memory, where the reader is
 * waiting on the mark. The handler's copy catches up on the next press that
 * lands, since a report against a stage the handler has not reached is stale
 * and answered with the kept places.
 *
 * **An answer says when the finish has handed the material to 复习**, and the
 * recorder remembers it for the line over the square. The block says it on
 * the way in for a drill finished before the page was opened.
 */
export class LianxiRecorder implements ExerciseKeeper {
  readonly #press: ExercisePress;
  readonly #characters: readonly string[];
  #handedOver: boolean;
  #handoverCount: HandoverCount | undefined;

  /** Whether the reader holds no Pro 专业, which the finish line names. */
  readonly free: boolean;

  /** The marker for the free stage: the ink posted, and the handler's mark. */
  readonly mark: CharacterMarker;

  constructor(properties: {
    /** Where a press posts, as the block says. */
    readonly address: string;

    /** The Exercise's slug, which every post names. */
    readonly slug: string;

    /** The characters, in the Exercise's order, for reading the places back. */
    readonly characters: readonly string[];

    readonly fetch: typeof fetch;

    /** Whether the reader holds no Pro 专业, as the block said. */
    readonly free?: boolean | undefined;

    /** Whether the block said the material was handed over already. */
    readonly handedOver?: boolean | undefined;

    /** How many cards the block said the hand-over put in, and when due. */
    readonly handoverCount?: HandoverCount | undefined;
  }) {
    this.#press = new ExercisePress(properties);
    this.#characters = properties.characters;
    this.#handedOver = properties.handedOver ?? false;
    this.#handoverCount = properties.handoverCount;
    this.free = properties.free ?? false;
    this.mark = remoteMarker(async (fields) => this.#post(fields));
  }

  /** Whether the finish has handed the material to 复习, as far as any answer said. */
  get handedOver(): boolean {
    return this.#handedOver;
  }

  /** How many cards the hand-over put in and when the first is due, as last said. */
  get handoverCount(): HandoverCount | undefined {
    return this.#handoverCount;
  }

  /** A pass done in a stage that snaps, reported with the stage it was in. */
  async passDone(
    character: string,
    from: DrillProgress,
  ): Promise<readonly DrillProgress[] | undefined> {
    const answer = await this.#post({
      [characterField]: character,
      [stageField]: String(from.stage),
    });

    return answer === undefined
      ? undefined
      : answeredPlaces(answer, this.#characters);
  }

  /** The Exercise started again. */
  async again(): Promise<void> {
    await this.#post({ [againField]: "" });
  }

  /** One press, and what its answer said about the hand-over. */
  async #post(
    fields: Readonly<Record<string, string>>,
  ): Promise<RecordAnswer | undefined> {
    const answer = await this.#press.post(fields);

    this.#handedOver = this.#handedOver || answer?.handedOver === true;
    this.#handoverCount = answerHandoverCount(answer) ?? this.#handoverCount;

    return answer;
  }
}
